<script lang="ts">
  /**
   * A CodeMirror 6 editor for lambda terms and programs.
   *
   * The editor owns its document; Svelte only pushes a value in when it changes
   * from the outside (switching level, resetting), which keeps typing smooth
   * and avoids feedback loops.
   *
   * The look is the same palette the rest of the site uses, read out of the CSS
   * variables in `app.css` rather than repeated here, so the two cannot drift
   * apart. Parentheses are coloured by nesting depth, a name that already
   * resolves is told apart from one that does not, and the name being defined
   * has its own colour, which together answer most of "what did I just write"
   * without running the checker.
   *
   * Six details matter for comfort:
   *
   * * the height follows the content up to a generous maximum, so a five-line
   *   program is not hidden behind a scrollbar;
   * * a selection shows a dot in every space it covers, and every indented line
   *   shows a line down each level of its indentation, so what a term is made of
   *   and how it is nested can be read off the page;
   * * Tab accepts a completion when one is offered and indents otherwise,
   *   because Tab is what everyone reaches for and the browser would otherwise
   *   move focus away;
   * * Shift-Enter opens a line below the current one for the next definition,
   *   and typing a paren with text selected wraps that text rather than
   *   replacing it;
   * * advice for an empty editor is rendered *beside* it rather than inside it.
   *   CodeMirror's own placeholder is an inline block aligned to the top of the
   *   line box, which puts it out of step with the cursor and makes an
   *   instruction look like content.
   *
   * The line height is set on the scroller, which is where CodeMirror sets its
   * own and everything inside it inherits from, and it is a whole number of
   * pixels that is also a whole number on the displays people use: four divides
   * 20, so 20 pixels are 20 device pixels at 1x, 25 at 1.25x, 30 at 1.5x and 40
   * at 2x. That matters because the browser paints a selection as one rectangle
   * per line, and rows
   * that land between device pixels leave a lighter line where two of them
   * meet, which reads as a gap. Setting it on the content alone instead would
   * leave CodeMirror measuring one line height and laying out another, and the
   * cursor would drift off the text; a lint underline does the same thing if it
   * carries padding, which is why the underline here is drawn by
   * `text-decoration` and takes up no space.
   *
   * There is no lint gutter for the same reason: a gutter's markers are as tall
   * as the line they belong to, and its own line height has to agree.
   */
  import {
    acceptCompletion,
    autocompletion,
    closeBrackets,
    closeBracketsKeymap,
    closeCompletion,
    completionKeymap,
    completionStatus,
    type Completion,
    type CompletionContext,
  } from '@codemirror/autocomplete';
  import {
    defaultKeymap,
    history,
    historyKeymap,
    indentLess,
    indentMore,
  } from '@codemirror/commands';
  import { bracketMatching, syntaxHighlighting, type MatchResult } from '@codemirror/language';
  import {
    EditorSelection,
    EditorState,
    StateEffect,
    StateField,
    Compartment,
    type Range,
    type SelectionRange,
  } from '@codemirror/state';
  import {
    Decoration,
    Direction,
    EditorView,
    keymap,
    layer,
    lineNumbers,
    RectangleMarker,
    ViewPlugin,
    type LayerMarker,
    type DecorationSet,
    type ViewUpdate,
  } from '@codemirror/view';
  import type { EditorDiagnostic } from '../lib/diagnostics';
  import {
    type Band,
    bandCorners,
    type Corners,
    indentGuides,
    selectedSpaces,
  } from '../lib/editor-marks';
  import { enclosingPair, lambdaHighlight, lambdaLanguageFor } from '../lib/lambda-language';
  import { toggleComments } from '../lib/comment';
  import type { Suggestion } from '../lib/suggestions';
  import { onMount } from 'svelte';

  interface Props {
    value: string;
    /**
     * The names worth completing, and which of them resolve. Unlike the props
     * below, this one stays live: the library it comes from changes while the
     * editor is open.
     */
    names?: Suggestion[];
    diagnostics?: EditorDiagnostic[];
    /** Shown under the editor while it is empty. */
    hint?: string;
    /** Decided when the editor is created; no caller changes it afterwards. */
    readOnly?: boolean;
    /** Decided when the editor is created; no caller changes it afterwards. */
    lineNumbers?: boolean;
    minHeight?: string;
    onchange?: (value: string) => void;
    onsubmit?: () => void;
  }

  let {
    value,
    names = [],
    diagnostics = [],
    hint = '',
    readOnly = false,
    lineNumbers: showLineNumbers = true,
    minHeight = '5.5rem',
    onchange,
    onsubmit,
  }: Props = $props();

  /** The parentheses this language has, for the two checks that ask. */
  const PARENTHESES = new Set(['(', ')']);

  /**
   * The marks that draw a pair of parentheses, given where its two brackets
   * are.
   *
   * The pair is put in reading order first, because CodeMirror reports the
   * bracket it found first: that is the closing one when the caret sits after
   * it, and the two cases differ only in which end is which.
   *
   * Two parentheses side by side are one box rather than two boxes with an edge
   * between them, and the halves it is drawn in have to be separate marks:
   * CodeMirror splits a mark wherever another decoration covers only part of
   * it, which is what the diagnostic under the closing parenthesis of `()` does
   * as soon as the parser has an opinion. Each half keeps the three edges it
   * owns and leaves out the one they would share, so nothing is drawn between
   * them.
   */
  function pairMarks(one: number, other: number, className: string): Range<Decoration>[] {
    const first = Math.min(one, other);
    const second = Math.max(one, other);
    const mark = (classes: string, from: number): Range<Decoration> =>
      Decoration.mark({ class: classes }).range(from, from + 1);
    return second === first + 1
      ? [mark(`${className} cm-bracket-left`, first), mark(`${className} cm-bracket-right`, second)]
      : [mark(className, first), mark(className, second)];
  }

  /** The one bracket a pair is missing, drawn as the mistake it is. */
  function mistakeMark(match: MatchResult): Range<Decoration>[] {
    return [
      Decoration.mark({ class: 'cm-bracket-mistake' }).range(match.start.from, match.start.to),
    ];
  }

  /**
   * The box drawn around the two parentheses of a pair, near the caret.
   *
   * The edge is drawn inside the character's own box, as a shadow rather than
   * an outline: an outline sits just outside the box, which makes the mark a
   * pixel bigger on every side than the character it is about, and a border
   * would move the character and the line it is on. Drawn inside, the wash
   * stops exactly at the edge, and the corners are the character's own.
   */
  const PAIR_BOX = {
    boxShadow: 'inset 0 0 0 1px var(--code-match)',
    backgroundColor: 'var(--code-match-fill)',
  };

  /**
   * One half of a pair whose two parentheses are side by side.
   *
   * The edge they would share is left out, and what remains is the outer edge
   * with the top and the bottom, which the halves draw separately and which
   * meet over the join.
   */
  const HALF_BOX = {
    left: {
      boxShadow:
        'inset 1px 0 0 var(--code-match), inset 0 1px 0 var(--code-match), inset 0 -1px 0 var(--code-match)',
    },
    right: {
      boxShadow:
        'inset -1px 0 0 var(--code-match), inset 0 1px 0 var(--code-match), inset 0 -1px 0 var(--code-match)',
    },
  };

  /**
   * Rules are written against `&.cm-editor` rather than the bare selector
   * because CodeMirror's own themes style the same elements, and the light and
   * dark variants of those rules carry as much weight as a plain selector does.
   * One more class settles it in favour of this theme, which is the one that
   * knows the site's palette.
   */
  const theme = EditorView.theme({
    '&': { backgroundColor: 'transparent', color: 'var(--code-text)', fontSize: '13.5px' },
    // See the note at the top of this file before changing this.
    '&.cm-editor .cm-scroller': { lineHeight: '20px' },
    '&.cm-editor .cm-content': {
      fontFamily: 'var(--mono)',
      padding: '8px 0',
      caretColor: 'var(--code-caret)',
    },
    /*
     * The selection is the browser's, as it always was; what is drawn here is
     * the cursor at the end of it, which the browser's own selection has none
     * of. It goes in a layer above the text, so it is visible over the
     * selection, and it blinks with the caret the browser draws when there is
     * no selection.
     */
    // The browser's own highlight is hidden; this file draws the selection.
    '&.cm-editor .cm-content ::selection': { backgroundColor: 'transparent' },
    '&.cm-editor .cm-selection-band': {
      position: 'absolute',
      backgroundColor: 'var(--code-selection)',
      pointerEvents: 'none',
    },
    // A fillet paints its own face, so it takes no colour of its own.
    '&.cm-editor .cm-selection-fillet': { position: 'absolute', pointerEvents: 'none' },
    '&.cm-editor:not(.cm-focused) .cm-selection-band': { opacity: '0.6' },
    // A dot in the spaces a selection covers, drawn over the selection and
    // quiet, so that it is there to be seen when looked for.
    '&.cm-editor .cm-space-dot': {
      backgroundColor: 'var(--code-space)',
      borderRadius: '50%',
      pointerEvents: 'none',
    },
    '&.cm-editor .cm-selectionCursor': {
      backgroundColor: 'var(--code-caret)',
      pointerEvents: 'none',
      animation: 'steps(1) cm-blink 1.2s infinite',
    },
    // No side padding: the text starts where the line numbers end. The gutter
    // keeps its own trailing padding, which is the gap a reader expects.
    '&.cm-editor .cm-line': { padding: '0' },
    '&.cm-focused': { outline: 'none' },
    '&.cm-editor .cm-gutters': {
      backgroundColor: 'transparent',
      border: 'none',
      color: 'var(--code-gutter)',
      fontSize: '12px',
      paddingRight: '10px',
    },
    '&.cm-editor .cm-activeLineGutter': {
      backgroundColor: 'transparent',
      color: 'var(--code-gutter-active)',
    },
    /*
     * An underline that cannot move a line: `text-decoration` is painted over
     * the text and takes up no space, unlike the background image with padding
     * under it that a lint underline uses, which changes the height of the line
     * it lands on and takes the cursor with it.
     */
    '&.cm-editor .cm-problem': { padding: '0', textDecoration: 'underline wavy var(--fail)' },
    '&.cm-editor .cm-activeLine': { backgroundColor: 'var(--code-line)' },
    /*
     * A pair is drawn as a thin box around each of its two parentheses, the way
     * an editor that colours bracket pairs does it: the box says which
     * characters belong together and leaves the character's own colour to say
     * how deep it is, which an opaque fill would paint over. An outline is what
     * keeps the box out of the layout, since a border would make the marked
     * character wider and move the rest of the line.
     */
    '&.cm-editor.cm-focused .cm-bracket-pair, &.cm-editor.cm-focused .cm-bracket-enclosing':
      PAIR_BOX,
    // The two halves of a side-by-side pair: the edge over the join belongs to
    // neither of them, so that `()` reads as one rectangle.
    '&.cm-editor.cm-focused .cm-bracket-left': HALF_BOX.left,
    '&.cm-editor.cm-focused .cm-bracket-right': HALF_BOX.right,
    // A parenthesis with no partner wears the same box; what says it has none
    // is the colour of the character itself.
    '&.cm-editor.cm-focused .cm-bracket-mistake': { ...PAIR_BOX, color: 'var(--fail)' },
    '&.cm-editor .cm-tooltip': {
      background: 'var(--bg-raised)',
      border: '1px solid var(--border-strong)',
      color: 'var(--text)',
      borderRadius: '6px',
      boxShadow: 'var(--shadow)',
    },
    '&.cm-editor .cm-tooltip-section:not(:first-child)': {
      borderTop: '1px solid var(--border)',
    },
    '&.cm-editor .cm-tooltip-arrow:before': {
      borderTopColor: 'var(--border-strong)',
      borderBottomColor: 'var(--border-strong)',
    },
    '&.cm-editor .cm-tooltip-arrow:after': {
      borderTopColor: 'var(--bg-raised)',
      borderBottomColor: 'var(--bg-raised)',
    },
    /*
     * The row the player is on is a surface rather than the accent, because the
     * accent is also what the letters they have typed are highlighted with: an
     * accent background under accent text is the one combination that cannot be
     * read. The bar on the left is what marks the row instead.
     */
    '&.cm-editor .cm-tooltip-autocomplete ul li[aria-selected]': {
      background: 'var(--code-row)',
      color: 'var(--text)',
      boxShadow: 'inset 2px 0 0 var(--accent)',
    },
    '&.cm-editor .cm-tooltip-autocomplete ul li[aria-selected] .cm-completionDetail': {
      color: 'var(--text-dim)',
    },
    '&.cm-editor .cm-completionDetail': {
      color: 'var(--text-faint)',
      fontStyle: 'normal',
      fontSize: '11px',
      marginLeft: '10px',
    },
    '&.cm-editor .cm-completionMatchedText': {
      textDecoration: 'none',
      color: 'var(--accent)',
      fontWeight: '600',
    },
  });

  /** The diagnostics to underline, pushed in from outside. */
  const setProblems = StateEffect.define<EditorDiagnostic[]>();

  /**
   * Turns diagnostics into underlines.
   *
   * An empty range (a parse error at the end of the input, usually) is widened
   * to one character so that there is something to see, and the ranges are
   * sorted here because diagnostics can arrive in any order.
   */
  function marksFor(list: EditorDiagnostic[], length: number): DecorationSet {
    const ranges = list.flatMap((entry) => {
      const start = Math.max(0, Math.min(entry.start, length));
      let end = Math.max(start, Math.min(entry.end, length));
      if (end === start) {
        end = start < length ? start + 1 : Math.max(0, start - 1);
      }
      if (end === start) {
        return [];
      }
      const mark = Decoration.mark({ class: 'cm-problem' });
      return [mark.range(Math.min(start, end), Math.max(start, end))];
    });
    return Decoration.set(ranges, true);
  }

  /** Keeps the underlines in step with the diagnostics, without touching the text. */
  const problems = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(value, transaction) {
      const kept = value.map(transaction.changes);
      for (const effect of transaction.effects) {
        if (effect.is(setProblems)) {
          return marksFor(effect.value, transaction.state.doc.length);
        }
      }
      return kept;
    },
    provide: (field) => EditorView.decorations.from(field),
  });

  /** How wide the cursor drawn at the end of a selection is, and how round
   * the dot in a space is. */
  const CURSOR_WIDTH = 2;
  const DOT = 2.5;

  /** Where the editor measures from, which is what a layer's markers draw in. */
  function scrollOrigin(view: EditorView): { left: number; top: number } {
    const box = view.scrollDOM.getBoundingClientRect();
    const left =
      view.textDirection === Direction.LTR ? box.left : box.right - view.scrollDOM.clientWidth;
    return { left: left - view.scrollDOM.scrollLeft, top: box.top - view.scrollDOM.scrollTop };
  }

  /** How far the corners of a selection band curve. */
  const CORNER = 3;

  /**
   * The selection, drawn here rather than left to the browser.
   *
   * A browser paints a selection as one rectangle per line, and at display
   * scales that are not whole numbers the rows can land between device pixels
   * and leave a lighter line where two of them meet. Drawing them here means
   * each row can be given its own corners and reach half a pixel into the rows
   * above and below, so they always overlap and never leave a gap. The rows
   * that continue over a line break also carry the break itself, one character
   * wide, which is what an editor shows and what a browser does not.
   *
   * The browser's own highlight is hidden in the theme; the selection itself is
   * still the browser's, so dragging, copying and the caret are unchanged.
   */
  const selection = layer({
    above: false,
    class: 'cm-drawnSelection',
    markers(view) {
      const marks: LayerMarker[] = [];
      for (const range of view.state.selection.ranges) {
        if (range.empty) {
          continue;
        }
        const bands = bandsOf(view, range);
        for (const [index, corners] of bandCorners(bands).entries()) {
          const band = bands[index];
          if (band !== undefined) {
            marks.push(new BandMarker(band, corners, scrollOrigin(view)));
          }
        }
      }
      return marks;
    },
    update(update) {
      return update.selectionSet || update.docChanged || update.viewportChanged;
    },
  });

  /** A coordinate on a whole device pixel, so that edges meet exactly. */
  function snap(value: number): number {
    const ratio = window.devicePixelRatio || 1;
    return Math.round(value * ratio) / ratio;
  }

  /**
   * The bands a selection covers, one per row of it from top to bottom.
   *
   * A row is a visual row, not a line: a line too long for the editor wraps
   * into several, and each of them is a band of its own with its own corners.
   * A row the selection carries on past gets its right edge from what comes
   * next: one character for a line break, which is what a row that ends a line
   * carries, and the edge of the code area for a wrap, where the same line
   * carries on below.
   */
  function bandsOf(view: EditorView, range: SelectionRange): Band[] {
    const { doc } = view.state;
    const first = doc.lineAt(range.from);
    const last = doc.lineAt(range.to);
    const edge = view.contentDOM.getBoundingClientRect().right;
    const bands: Band[] = [];
    for (let number = first.number; number <= last.number; number += 1) {
      const line = doc.line(number);
      const from = Math.max(range.from, line.from);
      // A line the selection leaves behind carries its line break.
      const carriesBreak = range.to > line.to;
      const limit = carriesBreak ? line.to : Math.min(range.to, line.to);
      if (from === limit && !carriesBreak) {
        continue;
      }
      for (const row of rowsOf(view, from, limit)) {
        const at = view.coordsAtPos(row.from);
        if (at === null) {
          continue;
        }
        // A row that wraps carries on below it, so it reaches the edge of the
        // code area; the last row of a line stops at its text. The end of that
        // one is measured from its last character, because the coordinates of
        // the position after a line report the row below.
        const ending = view.coordsAtPos(Math.max(row.from, row.to - 1));
        const right =
          row.wraps
            ? edge
            : ending === null
              ? at.left
              : ending.left + view.defaultCharacterWidth * (carriesBreak ? 2 : 1);
        bands.push({ top: at.top, bottom: at.bottom, left: at.left, right });
      }
    }
    // The height of a character's box is less than the height of the row, so
    // rows measured from it would leave a strip of the page between them. Each
    // pair meets in the middle of that strip instead.
    for (let index = 0; index + 1 < bands.length; index += 1) {
      const here = bands[index];
      const next = bands[index + 1];
      if (here === undefined || next === undefined) {
        continue;
      }
      const middle = (here.bottom + next.top) / 2;
      here.bottom = middle;
      next.top = middle;
    }
    // The edges are put on whole device pixels, so that two rows which meet
    // share an edge exactly and leave no line between them. The wash inside
    // them is translucent, so an overlap would show instead of a gap.
    return bands.map((band) => ({
      top: snap(band.top),
      bottom: snap(band.bottom),
      left: snap(band.left),
      right: snap(band.right),
    }));
  }

  /**
   * The visual rows a stretch of one line covers, as `[from, to)` positions,
   * each saying whether the line wraps at its end.
   *
   * Where a line wraps, the top of the coordinate the editor reports changes
   * between one position and the next; that is the boundary. The position at
   * the very end of the stretch is not one, since nothing follows it.
   */
  function rowsOf(
    view: EditorView,
    from: number,
    to: number,
  ): { from: number; to: number; wraps: boolean }[] {
    const rows: { from: number; to: number; wraps: boolean }[] = [];
    let start = from;
    let top = view.coordsAtPos(from)?.top ?? null;
    for (let pos = from + 1; pos <= to; pos += 1) {
      const at = view.coordsAtPos(pos);
      if (top === null || at === null || at.top === top || pos === to) {
        continue;
      }
      rows.push({ from: start, to: pos, wraps: true });
      start = pos;
      top = at.top;
    }
    rows.push({ from: start, to, wraps: false });
    return rows;
  }

  /**
   * Where each corner's fillet sits within a band, and where the disc it has
   * taken out of itself is centred. The four are in the order of `Corners`.
   *
   * A fillet belongs in the quadrant beside its corner that is not selected,
   * which is on the same side as the band that reaches past this one: a band
   * above wraps around the top corners from above, so the empty quadrant is
   * below them, and a band below wraps around the bottom corners from below,
   * so theirs is above. It is a square with a quarter disc removed from the
   * corner away from the band, which leaves a face that meets both edges of
   * the selection without a kink; a disc added at the corner instead would meet
   * each of them at a right angle and read as a bump rather than a curve.
   */
  const FILLETS: { left: number | string; top: number | string; at: string }[] = [
    { left: -CORNER, top: 0, at: '0% 100%' },
    { left: '100%', top: 0, at: '100% 100%' },
    { left: '100%', top: `calc(100% - ${CORNER}px)`, at: '100% 0%' },
    { left: -CORNER, top: `calc(100% - ${CORNER}px)`, at: '0% 0%' },
  ];

  /** One band of a selection, drawn with the corners it was given. */
  class BandMarker implements LayerMarker {
    readonly band: Band;
    readonly corners: Corners;
    readonly origin: { left: number; top: number };

    constructor(band: Band, corners: Corners, origin: { left: number; top: number }) {
      this.band = band;
      this.corners = corners;
      this.origin = origin;
    }

    draw(): HTMLElement {
      const box = document.createElement('div');
      box.className = 'cm-selection-band';
      box.style.left = `${this.band.left - this.origin.left}px`;
      box.style.top = `${this.band.top - this.origin.top}px`;
      box.style.width = `${this.band.right - this.band.left}px`;
      box.style.height = `${this.band.bottom - this.band.top}px`;
      box.style.borderRadius = this.corners
        .map((corner) => (corner === 'round' ? `${CORNER}px` : '0'))
        .join(' ');
      for (const [index, corner] of this.corners.entries()) {
        if (corner === 'inner') {
          box.appendChild(this.fillet(index));
        }
      }
      return box;
    }

    eq(other: unknown): boolean {
      return (
        other instanceof BandMarker &&
        other.band.left === this.band.left &&
        other.band.right === this.band.right &&
        other.band.top === this.band.top &&
        other.band.bottom === this.band.bottom &&
        other.corners.join() === this.corners.join()
      );
    }

    /**
     * The quarter circle that smooths the inside of a step.
     *
     * Each one is a square beside the corner it belongs to, in the empty
     * quadrant, with its opposite corner rounded away, which leaves a disc
     * centred on the corner itself.
     */
    private fillet(index: number): HTMLElement {
      const place: { left: number | string; top: number | string; at: string } = FILLETS[index] ?? {
        left: 0,
        top: 0,
        at: '0% 0%',
      };
      const piece = document.createElement('div');
      piece.className = 'cm-selection-fillet';
      piece.style.left = typeof place.left === 'number' ? `${place.left}px` : place.left;
      piece.style.top = typeof place.top === 'number' ? `${place.top}px` : place.top;
      piece.style.width = `${CORNER}px`;
      piece.style.height = `${CORNER}px`;
      piece.style.backgroundImage =
        `radial-gradient(circle at ${place.at}, transparent 0 ${CORNER}px,` +
        ` var(--code-selection) ${CORNER}px)`;
      return piece;
    }
  }

  /**
   * What is drawn over the text rather than in it: the cursor at the moving end
   * of a selection, and a dot in every space it covers.
   *
   * The browser paints a selection with no cursor in it, and it paints the
   * highlight over the backgrounds of the characters it covers, so neither can
   * be a mark on the character itself. Instead both are placed here, at
   * positions CodeMirror measures, in a layer above the text. Nothing else
   * about the selection is redrawn, so it keeps the look the browser gives it.
   */
  const overlay = layer({
    above: true,
    class: 'cm-overlay',
    markers(view) {
      const origin = scrollOrigin(view);
      const marks: LayerMarker[] = [];
      for (const range of view.state.selection.ranges) {
        if (!range.empty) {
          marks.push(...dotMarkers(view, range, origin));
        }
      }
      const cursor = view.hasFocus ? cursorMarker(view, origin) : null;
      if (cursor !== null) {
        marks.push(cursor);
      }
      return marks;
    },
    update(update) {
      return (
        update.selectionSet || update.docChanged || update.viewportChanged || update.focusChanged
      );
    },
  });

  /** The cursor drawn at the end of a selection, or nothing without one. */
  function cursorMarker(
    view: EditorView,
    origin: { left: number; top: number },
  ): LayerMarker | null {
    const range = view.state.selection.main;
    if (range.empty) {
      return null;
    }
    const at = view.coordsAtPos(range.head, range.assoc || 1);
    if (at === null) {
      return null;
    }
    return new RectangleMarker(
      'cm-selectionCursor',
      at.left - origin.left - CURSOR_WIDTH / 2,
      at.top - origin.top,
      CURSOR_WIDTH,
      at.bottom - at.top,
    );
  }

  /** A dot in the middle of every space the given range covers on screen. */
  function dotMarkers(
    view: EditorView,
    range: SelectionRange,
    origin: { left: number; top: number },
  ): LayerMarker[] {
    const marks: LayerMarker[] = [];
    for (const visible of view.visibleRanges) {
      const from = Math.max(range.from, visible.from);
      const to = Math.min(range.to, visible.to);
      for (const pos of selectedSpaces(view.state, from, to)) {
        const at = view.coordsAtPos(pos);
        const after = view.coordsAtPos(pos + 1);
        if (at === null || after === null) {
          continue;
        }
        marks.push(
          new RectangleMarker(
            'cm-space-dot',
            (at.left + after.left) / 2 - origin.left - DOT / 2,
            at.top - origin.top + (at.bottom - at.top) * 0.55 - DOT / 2,
            DOT,
            DOT,
          ),
        );
      }
    }
    return marks;
  }

  /**
   * The line the caret is on, marked while nothing is selected.
   *
   * Its background is painted by the line itself, over the layer a selection is
   * drawn in, so marking it during a selection would hide the selection on the
   * very line the caret is on.
   */
  const activeLine = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = this.marks(view.state);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.selectionSet) {
          this.decorations = this.marks(update.state);
        }
      }

      marks(state: EditorState): DecorationSet {
        // A read-only area has no caret of its own: the row a click lands on
        // is text to read, and painting it would say otherwise.
        if (!state.facet(EditorView.editable)) {
          return Decoration.none;
        }
        const range = state.selection.main;
        if (!range.empty) {
          return Decoration.none;
        }
        const line = state.doc.lineAt(range.head);
        return Decoration.set([Decoration.line({ class: 'cm-activeLine' }).range(line.from)], true);
      }
    },
    { decorations: (plugin) => plugin.decorations },
  );

  /**
   * The lines down the levels of indentation, redrawn when the document or the
   * lines on screen change, and not when the selection does.
   */
  const guides = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = this.over(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.over(update.view);
        }
      }

      /** The guides of the lines on screen, which are the ones that are drawn. */
      over(view: EditorView): DecorationSet {
        const marks = view.visibleRanges.flatMap((range) =>
          indentGuides(view.state, range.from, range.to),
        );
        return Decoration.set(marks, true);
      }
    },
    { decorations: (plugin) => plugin.decorations },
  );

  /** Whether a parenthesis sits directly either side of the caret. */
  function besideAParenthesis(state: EditorState, head: number): boolean {
    const before = head > 0 ? state.sliceDoc(head - 1, head) : '';
    const after = head < state.doc.length ? state.sliceDoc(head, head + 1) : '';
    return PARENTHESES.has(before) || PARENTHESES.has(after);
  }

  /**
   * Boxes the pair of parentheses the caret is inside.
   *
   * CodeMirror's own matcher marks the bracket beside the caret and the one it
   * matches; what it cannot say is which pair the caret is in when no bracket
   * is beside it, and that is where a nested term spends most of its time. This
   * stands down whenever a parenthesis is beside the caret, so the two never
   * mark a pair between them.
   */
  function enclosingMarks(state: EditorState): DecorationSet {
    const marks: Range<Decoration>[] = [];
    for (const range of state.selection.ranges) {
      if (!range.empty || besideAParenthesis(state, range.head)) {
        continue;
      }
      const pair = enclosingPair(state.doc, range.head);
      if (pair === null) {
        continue;
      }
      marks.push(...pairMarks(pair.open, pair.close, 'cm-bracket-enclosing'));
    }
    return Decoration.set(marks, true);
  }

  const enclosing = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = enclosingMarks(view.state);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.selectionSet) {
          this.decorations = enclosingMarks(update.state);
        }
      }
    },
    { decorations: (plugin) => plugin.decorations },
  );

  let container: HTMLDivElement | undefined = $state();
  let view: EditorView | undefined;

  /**
   * The extensions that are replaced while the editor lives: which names
   * resolve, and which are worth completing. Both come from the library, which
   * changes as the player types, and a compartment swaps them without touching
   * the document or the caret.
   */
  const librarySlot = new Compartment();

  /** The names that resolve, which is what the tokenizer colours differently. */
  function knownIn(candidates: readonly Suggestion[]): ReadonlySet<string> {
    return new Set(candidates.filter((entry) => entry.defined === true).map((entry) => entry.name));
  }

  function completions(candidates: readonly Suggestion[]) {
    return (context: CompletionContext) => {
      const word = context.matchBefore(/[A-Za-z_][A-Za-z0-9_']*/);
      if (word === null || (word.from === word.to && !context.explicit)) {
        return null;
      }
      return {
        from: word.from,
        options: candidates.map(
          (entry): Completion => ({
            label: entry.name,
            // The level's own slots are what the player has to write; a name
            // that already resolves is a library definition.
            type: entry.defined === true ? 'function' : 'variable',
            detail: entry.detail,
            sortText: `${entry.rank ?? 1} ${entry.name}`,
          }),
        ),
      };
    };
  }

  /**
   * Work that must not touch the editor until the keyboard has settled.
   *
   * A phone keyboard composes before it commits: the text is in the DOM while
   * the editor has not been told about it yet. Dispatching anything in that
   * window makes CodeMirror read the DOM back, and on Android that loses the
   * composing run, which is what "some of the text erases itself a second
   * after I type it" is. The squiggles, the name list and a value arriving
   * from elsewhere therefore wait for `compositionend`.
   *
   * Each entry reads what it needs when it runs rather than when it was
   * queued: by then the value, the diagnostics and the names have all moved
   * on, and applying the older ones would undo what the player just typed.
   */
  const heldBack = new Map<string, () => void>();

  /** Whether the keyboard has a word in progress in this editor. */
  let composing = $state(false);

  /** Runs `work` now, or holds it back until the composition in progress ends. */
  function whenSettled(target: EditorView, what: string, work: () => void): void {
    if (!target.composing && !target.compositionStarted) {
      work();
      return;
    }
    heldBack.set(what, work);
  }

  /**
   * Opens an empty line below the caret's, at the margin.
   *
   * Shift-Enter is how a player starts the next definition without cutting the
   * line they are on in two, so the line it opens belongs at the left margin
   * whatever the line above it looks like.
   */
  function openDefinitionLine(view: EditorView): boolean {
    const line = view.state.doc.lineAt(view.state.selection.main.head);
    view.dispatch({
      changes: { from: line.to, insert: '\n' },
      selection: EditorSelection.cursor(line.to + 1),
      scrollIntoView: true,
      userEvent: 'input',
    });
    return true;
  }

  /**
   * Puts parentheses around a selection instead of replacing it.
   *
   * Typing an opening paren with text selected means "wrap this". The closing
   * paren does not: a player who types one is closing a term they have already
   * opened, and wrapping as well would put two brackets in the text where they
   * asked for one, so it replaces the selection like any other character. An
   * empty cursor is left alone either way, and so is every other key.
   */
  function wrapSelection(target: EditorView, text: string): boolean {
    if (text !== '(') {
      return false;
    }
    // Wrapping dispatches, and CodeMirror offers the composing text of a phone
    // keyboard to the input handlers as it arrives: doing it then is the same
    // interruption as the squiggles used to cause, so the keyboard's text is
    // left to the keyboard.
    if (target.composing || target.compositionStarted) {
      return false;
    }
    const { state } = target;
    if (state.selection.ranges.some((range) => range.empty)) {
      return false;
    }
    target.dispatch(
      state.changeByRange((range) => ({
        changes: [
          { from: range.from, insert: '(' },
          { from: range.to, insert: ')' },
        ],
        range: EditorSelection.range(range.from + 1, range.to + 1),
      })),
    );
    return true;
  }

  onMount(() => {
    if (container === undefined) return;
    const host = container;
    view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: value,
        extensions: [
          history(),
          keymap.of([
            {
              /*
               * Checking and then leaving in one keystroke: the report is where
               * the player looks next, and the level keys are the page's again
               * the moment the editor lets go.
               */
              key: 'Mod-Enter',
              run: (target) => {
                onsubmit?.();
                target.contentDOM.blur();
                return true;
              },
            },
            {
              /*
               * Enter splits the line at the caret, which is the wrong thing
               * between two definitions; this opens a fresh line below the
               * current one and leaves the text where it is. The line it opens
               * is a definition line, so it stays at the margin: copying the
               * indentation of the line above would put it inside that
               * definition rather than beside it.
               */
              key: 'Shift-Enter',
              run: openDefinitionLine,
            },
            {
              // A line set aside without deleting it, and a selection of them.
              key: 'Mod-/',
              run: toggleComments,
            },
            {
              /*
               * Escape lets go of the editor in two stages: the first press
               * dismisses the completion list, the next drops the selection
               * and blurs, so that the level shortcuts work without reaching
               * for the mouse. A run of Escape presses therefore does the
               * thing the player meant at the time.
               */
              key: 'Escape',
              run: (target) => {
                if (completionStatus(target.state) !== null) {
                  return closeCompletion(target);
                }
                target.dispatch({
                  selection: EditorSelection.cursor(target.state.selection.main.head),
                });
                target.contentDOM.blur();
                return true;
              },
            },
            {
              key: 'Tab',
              run: (target) => {
                if (completionStatus(target.state) === 'active') {
                  return acceptCompletion(target);
                }
                return indentMore(target);
              },
              shift: indentLess,
            },
            ...closeBracketsKeymap,
            ...completionKeymap,
            ...defaultKeymap,
            ...historyKeymap,
          ]),
          syntaxHighlighting(lambdaHighlight),
          showLineNumbers ? lineNumbers() : [],
          // The selection itself, and then the cursor and dots, which have to
          // be drawn over it.
          selection,
          overlay,
          activeLine,
          guides,
          // CodeMirror decides which pair the caret is involved with, looking
          // on either side of it; how that pair is drawn is this file's.
          bracketMatching({
            brackets: '()',
            renderMatch: (match) =>
              match.matched && match.end !== undefined
                ? pairMarks(match.start.from, match.end.from, 'cm-bracket-pair')
                : mistakeMark(match),
          }),
          // And the pair the caret is inside, when it is beside none.
          enclosing,
          // Before `closeBrackets`, so wrapping a selection wins over inserting
          // a pair around an empty cursor, and so the closing paren reaches
          // `closeBrackets` as the plain insertion it is.
          EditorView.inputHandler.of((target, _from, _to, text) => wrapSelection(target, text)),
          closeBrackets(),
          librarySlot.of([
            lambdaLanguageFor(knownIn(names)),
            autocompletion({ override: [completions(names)] }),
          ]),
          problems,
          EditorView.lineWrapping,
          EditorView.editable.of(!readOnly),
          EditorState.readOnly.of(readOnly),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              onchange?.(update.state.doc.toString());
            }
          }),
          /*
           * The selection belongs to the editor that has the focus. On the way
           * out it is let go: the caret stays where it was, and the painted
           * band with it, so a selection never outlives the editor it was
           * made in. The handler runs outside the update cycle, which is what
           * lets it dispatch.
           */
          EditorView.domEventHandlers({
            blur: (_event, view) => {
              const { ranges } = view.state.selection;
              if (!ranges.some((range) => !range.empty)) {
                return false;
              }
              view.dispatch({
                selection: EditorSelection.create(
                  ranges.map((range) => EditorSelection.cursor(range.head)),
                  view.state.selection.mainIndex,
                ),
              });
              return false;
            },
          }),
          theme,
        ],
      }),
    });
    /*
     * A read-only area is never the focused element, so its selection never
     * sees a blur; the document's own selection is watched instead, and the
     * moment it moves out of this view the selection is let go. A selection
     * made inside stays, because its anchor is still here.
     */
    const documentSelectionChanged = () => {
      const current = view;
      if (current === undefined || current.hasFocus) {
        return;
      }
      if (current.state.selection.ranges.every((range) => range.empty)) {
        return;
      }
      const selection = document.getSelection();
      if (selection === null || selection.anchorNode === null) {
        return;
      }
      if (current.contentDOM.contains(selection.anchorNode)) {
        return;
      }
      const { ranges } = current.state.selection;
      current.dispatch({
        selection: EditorSelection.create(
          ranges.map((range) => EditorSelection.cursor(range.head)),
          current.state.selection.mainIndex,
        ),
      });
    };
    document.addEventListener('selectionchange', documentSelectionChanged);
    /*
     * The composition is over, so whatever was waiting for it can go in. The
     * dispatch follows the event rather than happening inside it: CodeMirror
     * clears its composing flag while handling the event, and a dispatch from
     * within that handling is the thing this exists to avoid.
     */
    const compositionEnded = () => {
      queueMicrotask(() => {
        const target = view;
        if (target === undefined || target.composing || target.compositionStarted) {
          // Still composing: the marks on show are still the old ones, so they
          // stay out of sight.
          return;
        }
        const waiting = [...heldBack.values()];
        heldBack.clear();
        for (const work of waiting) {
          work();
        }
        // After the fresh marks are in, so the old ones never come back for a
        // frame on the way out.
        composing = false;
      });
    };
    /*
     * A composition is the keyboard's word in progress. The squiggles on show
     * were computed from the text before it, so they sit under the wrong
     * characters until it commits; while it is open they are painted out. It
     * is a paint change and nothing else - no dispatch, no DOM inside the
     * editor, no reflow - which is what keeps the composition itself safe.
     */
    const compositionStarted = () => {
      composing = true;
    };
    host.addEventListener('compositionstart', compositionStarted);
    host.addEventListener('compositionend', compositionEnded);
    return () => {
      document.removeEventListener('selectionchange', documentSelectionChanged);
      host.removeEventListener('compositionstart', compositionStarted);
      host.removeEventListener('compositionend', compositionEnded);
      view?.destroy();
      view = undefined;
    };
  });

  // Adopt values that changed elsewhere without disturbing local typing.
  $effect(() => {
    // Read here so that a new value re-runs the effect; the work below reads
    // it again when it runs, which is the value that should win.
    void value;
    const target = view;
    if (target === undefined) {
      return;
    }
    whenSettled(target, 'value', () => {
      const next = value;
      if (view === target && target.state.doc.toString() !== next) {
        target.dispatch({ changes: { from: 0, to: target.state.doc.length, insert: next } });
      }
    });
  });

  // Keep the squiggles in step with what the engine last reported.
  $effect(() => {
    // As with the value: subscribed here, read again when it is applied.
    void diagnostics;
    const target = view;
    if (target === undefined) {
      return;
    }
    queueMicrotask(() => {
      if (view === target) {
        whenSettled(target, 'problems', () =>
          target.dispatch({ effects: setProblems.of(diagnostics) }),
        );
      }
    });
  });

  /**
   * Keeps the completion list and the name colours in step with `names`.
   *
   * The names change whenever the library does, which is often while the player
   * is typing, and the same list under a new name is not a change: only a
   * different set is worth swapping the extension for, because a reconfigure
   * closes a completion list that is open.
   */
  let offered: Suggestion[] | null = null;
  $effect(() => {
    const target = view;
    const wanted = names;
    if (target === undefined) {
      return;
    }
    const same =
      offered !== null &&
      offered.length === wanted.length &&
      offered.every((entry, index) => {
        const other = wanted[index];
        return (
          other !== undefined &&
          entry.name === other.name &&
          entry.detail === other.detail &&
          entry.rank === other.rank &&
          entry.defined === other.defined
        );
      });
    if (same) {
      return;
    }
    whenSettled(target, 'names', () => {
      const latest = names;
      const already =
        offered !== null &&
        offered.length === latest.length &&
        offered.every((entry, index) => {
          const other = latest[index];
          return other !== undefined && entry.name === other.name && entry.rank === other.rank;
        });
      if (already) {
        return;
      }
      offered = latest.map((entry) => ({ ...entry }));
      target.dispatch({
        effects: librarySlot.reconfigure([
          lambdaLanguageFor(knownIn(latest)),
          autocompletion({ override: [completions(latest)] }),
        ]),
      });
    });
  });
</script>

<div class="frame">
  <div
    class="editor"
    class:composing
    class:read-only={readOnly}
    class:no-gutter={!showLineNumbers}
    style={`--editor-min: ${minHeight};`}
    bind:this={container}
  ></div>
  {#if hint !== '' && value.trim() === ''}
    <p class="hint">{hint}</p>
  {/if}
</div>

<style>
  .frame {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .editor {
    background: var(--code-bg);
    border: 1px solid var(--border-strong);
    border-radius: 6px;
    overflow: hidden;
  }

  .editor:focus-within {
    border-color: var(--accent);
  }

  .editor.read-only {
    border-color: var(--border);
  }

  /*
   * While the keyboard is composing, the squiggles are from the text before the
   * word being typed, so they point at the wrong characters. Only their paint
   * is turned off: the layout is untouched, which is what keeps a composition
   * from being disturbed.
   */
  .editor.composing :global(.cm-problem) {
    text-decoration-color: transparent;
  }

  /* Without a gutter there is nothing to align to, so the text is inset. */
  .editor.no-gutter :global(.cm-content) {
    padding-left: 10px;
  }

  /*
   * The editor is a flex column, so the scroller is told to fill it. Without
   * that the scroller is only as tall as the text, and the visible box is
   * bigger than the element the mouse events reach.
   *
   * There is no maximum height: the box grows with the program and the page is
   * what scrolls, so a hundred lines are a hundred lines rather than a window
   * onto twenty of them.
   */
  .editor :global(.cm-editor) {
    display: flex;
    flex-direction: column;
    min-height: var(--editor-min);
  }

  .editor :global(.cm-scroller) {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
  }

  /*
   * The content and the gutter have to cover the whole scroller, not just their
   * own lines. CodeMirror listens for the mouse on the content element, and its
   * own handling turns a click below the last line into "the end of the
   * document", but a click that lands on the scroller instead never reaches
   * it, so the editor takes focus and no caret appears. The base theme's
   * `min-height: 100%` does not achieve this, because the height it would
   * resolve against is not definite; stretching the flex item does.
   */
  .editor :global(.cm-content),
  .editor :global(.cm-gutters) {
    align-self: stretch;
  }

  .hint {
    color: var(--text-faint);
    font-size: 13px;
    margin: 0 2px;
  }
</style>
